/**
 * DatePicker Component
 * Date input with custom styling (uses native HTML5 date input)
 */

import { InputHTMLAttributes } from 'react';
import { CalendarIcon } from '@heroicons/react/24/outline';
import { cn } from '@/utils/cn';

export interface DatePickerProps extends Omit<InputHTMLAttributes<HTMLInputElement>, 'type'> {
  label: string;
  error?: string;
  helpText?: string;
  containerClassName?: string;
}

export default function DatePicker({
  label,
  error,
  helpText,
  containerClassName,
  className,
  id,
  disabled,
  required,
  ...props
}: DatePickerProps) {
  const inputId = id || `date-${label.toLowerCase().replace(/\s+/g, '-')}`;
  const hasError = !!error;

  return (
    <div className={cn('w-full', containerClassName)}>
      {/* Label */}
      <label
        htmlFor={inputId}
        className="block text-sm font-medium text-foreground mb-1.5"
      >
        {label}
        {required && <span className="text-danger ml-1">*</span>}
      </label>

      {/* Date Input Container */}
      <div className="relative">
        <input
          type="date"
          id={inputId}
          disabled={disabled}
          required={required}
          aria-invalid={hasError}
          aria-describedby={
            hasError ? `${inputId}-error` : helpText ? `${inputId}-help` : undefined
          }
          className={cn(
            // Base styles
            'w-full h-11 px-3 pr-10 rounded-md border',
            'text-sm text-foreground bg-input',
            'transition-all duration-200',
            'focus:outline-none focus:ring-3 focus:ring-primary/20',

            // States
            !hasError && 'border-border',
            !hasError && 'focus:border-primary',
            hasError && 'border-danger focus:border-danger focus:ring-danger/20',
            disabled && 'opacity-60 cursor-not-allowed bg-muted',

            // Custom class
            className
          )}
          {...props}
        />

        {/* Calendar Icon */}
        <div className="absolute right-3 top-1/2 -translate-y-1/2 pointer-events-none">
          <CalendarIcon
            className={cn(
              'w-5 h-5',
              hasError ? 'text-danger' : 'text-foreground/40'
            )}
          />
        </div>
      </div>

      {/* Helper/Error Text */}
      {(error || helpText) && (
        <p
          id={error ? `${inputId}-error` : `${inputId}-help`}
          className={cn(
            'mt-1.5 text-sm',
            error ? 'text-danger' : 'text-foreground/60'
          )}
        >
          {error || helpText}
        </p>
      )}
    </div>
  );
}
